import { FormControl, Grid, TextField } from "@mui/material";
import { FC, useEffect, useState } from "react";
import { createDefaultVital } from ".";
import SelectButton from "../../../../../../components/shortcuts/components/SelectButton";
import { VitalsInputProps } from "../../../../../../components/widgets/modals/encounter";
import CodingSystems from "../../../../../../constants/CodingSystems";
import usePractitioner from "../../../../../../hooks/usePractitioner";
import { useMedicalConfig } from "../../../../../../slices/configuration";
import { Vital } from "../../../../../../types/Vitals";
import { testRegExp } from "../../../../../../utils/regex";
import { updateMetaData } from "../VitalsFormHelper";

const HeightInput: FC<VitalsInputProps> = ({
  encounter,
  patient,
  disabled,
  value,
  onChange,
  size = "medium",
}) => {
  const practitionerReference = usePractitioner();
  const { vitalMeasurement } = useMedicalConfig();
  const [height, setHeight] = useState<Vital>();
  const currentUnit =
    value?.valueQuantity?.unit ?? vitalMeasurement?.height ?? "cm";

  // Add state for feet and inches
  const [feet, setFeet] = useState<string>("");
  const [inches, setInches] = useState<string>("");

  useEffect(() => {
    if (value) {
      setHeight(
        createDefaultVital(
          encounter,
          patient,
          CodingSystems.OBSERVATION_CODE,
          "8302-2",
          "Body Height",
          value?.valueQuantity?.unit ?? vitalMeasurement?.height ?? "cm",

          Math.round(parseInt(value?.valueQuantity?.value)),
        ),
      );
      return;
    }
    const ht = createDefaultVital(
      encounter,
      patient,
      CodingSystems.OBSERVATION_CODE,
      "8302-2",
      "Body Height",
      vitalMeasurement?.height ?? "cm",
    );
    setHeight(ht);
  }, [encounter, patient, value, vitalMeasurement]);

  // Convert total inches to feet and inches for display
  const inchesToFeetAndInches = (totalInches: number | string) => {
    if (!totalInches || currentUnit !== "in") return { feet: "", inches: "" };
    const numInches = Number(totalInches);
    return {
      feet: Math.floor(numInches / 12).toString(),
      inches: (numInches % 12).toString(),
    };
  };

  // Convert feet and inches to total inches
  const feetAndInchesToTotalInches = (feet: string, inches: string) => {
    const numFeet = Number(feet) || 0;
    const numInches = Number(inches) || 0;
    return (numFeet * 12 + numInches).toString();
  };

  // Update feet/inches state when value changes
  useEffect(() => {
    if (currentUnit === "in" && value?.valueQuantity?.value) {
      const { feet: ft, inches: inc } = inchesToFeetAndInches(
        value.valueQuantity.value,
      );
      setFeet(ft);
      setInches(inc);
    }
  }, [value, currentUnit]);

  const centimeterToWholeNumber = (value: number | string) => {
    if (currentUnit === "cm") {
      return value ? Number(value) : "";
    }

    return value ?? "";
  };

  // Add conversion functions
  const cmToInches = (cm: number) => Math.round(cm / 2.54);
  const inchesToCm = (inches: number) => Math.round(inches * 2.54);

  return (
    <Grid item xs={12}>
      <FormControl fullWidth>
        <Grid container spacing={1}>
          {currentUnit === "in" ? (
            <>
              <Grid item xs={4}>
                <TextField
                  disabled={disabled}
                  value={feet}
                  onChange={(e) => {
                    const newFeet = e.target.value;
                    const valid = testRegExp(
                      "non-negative integer or empty string",
                      newFeet,
                    );
                    if (!valid) return;
                    setFeet(newFeet);

                    const totalInches = feetAndInchesToTotalInches(
                      newFeet,
                      inches,
                    );
                    const newHeight = createDefaultVital(
                      encounter,
                      patient,
                      CodingSystems.OBSERVATION_CODE,
                      "8302-2",
                      "Body Height",
                      "in",
                      totalInches,
                    );
                    updateMetaData(newHeight, practitionerReference);
                    onChange({
                      updated: true,
                      ...value,
                      ...newHeight,
                    });
                  }}
                  label="ft"
                  autoComplete="off"
                  variant="outlined"
                  size={size || "medium"}
                  InputProps={{
                    sx: {
                      borderRadius: "8px 0px 0px 8px",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  disabled={disabled}
                  value={inches}
                  onChange={(e) => {
                    const newInches = e.target.value;
                    const valid = testRegExp(
                      "non-negative integer or empty string",
                      newInches,
                    );
                    if (!valid) return;
                    if (Number(newInches) >= 12) return;
                    setInches(newInches);

                    const totalInches = feetAndInchesToTotalInches(
                      feet,
                      newInches,
                    );
                    const newHeight = createDefaultVital(
                      encounter,
                      patient,
                      CodingSystems.OBSERVATION_CODE,
                      "8302-2",
                      "Body Height",
                      "in",
                      totalInches,
                    );
                    updateMetaData(newHeight, practitionerReference);
                    onChange({
                      updated: true,
                      ...value,
                      ...newHeight,
                    });
                  }}
                  label="in"
                  autoComplete="off"
                  variant="outlined"
                  size={size || "medium"}
                  sx={{ marginRight: -1 }}
                  InputProps={{
                    sx: {
                      borderRadius: "0px 8px 8px 0px",
                    },
                  }}
                />
              </Grid>
            </>
          ) : (
            <Grid item xs={8}>
              <TextField
                disabled={disabled}
                value={centimeterToWholeNumber(value?.valueQuantity?.value)}
                onChange={(e) => {
                  const newHeightValue = e.target.value;
                  const valid = testRegExp(
                    "non-negative decimal number or empty string",
                    newHeightValue,
                  );
                  if (!valid) return;

                  const newHeight = createDefaultVital(
                    encounter,
                    patient,
                    CodingSystems.OBSERVATION_CODE,
                    "8302-2",
                    "Body Height",
                    currentUnit,
                    newHeightValue,
                  );
                  updateMetaData(newHeight, practitionerReference);
                  onChange({
                    updated: true,
                    ...value,
                    ...newHeight,
                  });
                }}
                id="vitals-height-cm"
                label="Height"
                autoComplete="off"
                variant="outlined"
                size={size || "medium"}
                type="number"
                sx={{ marginRight: -1 }}
                inputProps={{
                  min: "0",
                  step: "0.01",
                }}
              />
            </Grid>
          )}
          <Grid item xs={4}>
            <SelectButton
              label={currentUnit === "in" ? "ft,in" : "cm"}
              options={[
                { label: "cm", value: "cm" },
                { label: "ft,in", value: "in" },
              ]}
              variant="text"
              disabled={disabled}
              onSelect={(unit) => {
                if (!value?.valueQuantity?.value) {
                  // If no value, just update the unit
                  const temp = createDefaultVital(
                    encounter,
                    patient,
                    CodingSystems.OBSERVATION_CODE,
                    "8302-2",
                    "Body Height",
                    unit?.value,
                    value?.valueQuantity?.value,
                  );
                  updateMetaData(temp, practitionerReference);
                  onChange(temp);
                  return;
                }

                // Convert the value based on the unit change
                const currentValue = Number(value.valueQuantity.value);
                let newValue: number;

                if (unit?.value === "in" && value.valueQuantity.unit === "cm") {
                  newValue = cmToInches(currentValue);
                } else if (
                  unit?.value === "cm" &&
                  value.valueQuantity.unit === "in"
                ) {
                  newValue = inchesToCm(currentValue);
                } else {
                  newValue = currentValue;
                }

                const temp = createDefaultVital(
                  encounter,
                  patient,
                  CodingSystems.OBSERVATION_CODE,
                  "8302-2",
                  "Body Height",
                  unit?.value,
                  newValue.toString(),
                );

                updateMetaData(temp, practitionerReference);
                onChange(temp);
              }}
              size={"medium"}
              getOptionLabel={(value) => value?.label}
            />
          </Grid>
        </Grid>
      </FormControl>
    </Grid>
  );
};

export default HeightInput;
